import { API_BASE_URL } from '../utils/api'
import type { TraceStep, TraceSummary } from '../components/flowDebugger/types'
import type {
  TestChatEvaluationResult,
  TestWebchatSession,
  SendTestMessageResult,
} from '../../lib/api/botSettings'

export type { TestChatEvaluationResult, TestWebchatSession, SendTestMessageResult }

type HitlResumeAction = 'approve' | 'edit' | 'deny'

export interface HitlResumePayload {
  tenantId: string
  conversationId: string
  intent?: string
  stage?: string
  resume: {
    action: HitlResumeAction
    message?: string
    reason?: string
  }
}

export interface HitlResumeResponse {
  data: {
    conversation: {
      id: string
      tenantId: string
      mode: string
      channelType: string
    }
    result: {
      success: boolean
      pendingResponse: string | null
      durationMs: number
      intent: string
      route: string
      finalRoute: string
      hitlPaused?: boolean
      hitlRequest?: unknown
      error?: string
      state: Record<string, unknown>
    }
  }
}

export interface ObservabilitySummaryResponse {
  data: {
    windowMinutes: number
    totalTraces: number
    fallbackRate: number
    escalationRate: number
    loopRate: number
    avgLatencyMs: number
    latencyP95Ms: number
    checkpointRestoreFailures: number
    checkpointRestoreFailureRate: number
    cragRejectRate: number
    hitlTriggerRate: number
    activeLongConversations: number
    activeConversations: number
    blockedFunnelRegressions: number
    checkoutPromotions: number
    checkoutPromotionBlocked: number
    supportObjectionCount: number
    acknowledgeRejectionCount: number
    browseLoopCount: number
    purchaseOscillationCount: number
    verticalBreakdown: ObservabilityVerticalBreakdownRow[]
    verticalGuardrailCounts: Record<string, number>
  }
}

export interface ObservabilityVerticalBreakdownRow {
  vertical: string
  traceCount: number
  frictionCount: number
  frictionRate: number
  fallbackCount: number
  fallbackRate: number
  loopCount: number
  loopRate: number
  escalationCount: number
  escalationRate: number
  verticalGuardrailCount: number
  verticalGuardrailRate: number
  supportObjectionCount: number
  acknowledgeRejectionCount: number
  avgLatencyMs: number | null
  p95LatencyMs: number | null
}

export interface ObservabilityAlert {
  id: string
  severity: 'critical' | 'warning' | 'info'
  type: string
  message: string
  tenantId: string
  status: 'active' | 'acknowledged' | 'resolved'
  firstSeenAt: string
  lastSeenAt: string
  affectedConversations: number
  affectedConversationIds?: string[]
  /** Contexto adicional de la regla que disparó la alerta (ej: simulación del supervisor). */
  metadata?: Record<string, unknown>
}

export interface ObservabilityAlertResponse {
  data: ObservabilityAlert[]
}

export interface ObservabilityFunnelResponse {
  data: {
    windowMinutes: number
    stageCounts: Record<string, number>
    stageDropoff: Record<string, number>
    checkoutPromotions: number
    funnelOverridesApplied: number
    funnelOverridesBlocked: number
    avgCheckoutMomentum: number
    avgClosingReadinessIndex: number
    authorityModeBreakdown: Record<string, number>
  }
}

export interface ObservabilityConversationRow {
  tenantId: string
  conversationId: string
  turns: number
  currentStage: string | null
  stagnationTurns: number
  lastActivity: string
  lastActivityAt?: string
  health?: 'healthy' | 'warning' | 'degraded' | 'critical'
  healthStatus: 'healthy' | 'warning' | 'degraded' | 'critical'
  summaryCount: number
  checkpointSizeBytes: number | null
  strategicSnapshotCount: number
  memoryPruningEvents: number
  repeatedNodePattern: string | null
  noProgressTurns: number
}

export interface ObservabilityConversationsResponse {
  data: ObservabilityConversationRow[]
}

export type ObservabilityAnalysisScope = 'debug' | 'observability' | 'alerts' | 'funnel' | 'conversations'

export type ObservabilityAnalysisProvider =
  | 'auto'
  | 'openai'
  | 'anthropic'
  | 'mistral'
  | 'deepseek'
  | 'gemini'
  | 'groq'
  | 'nvidia'
  | 'openrouter'
  | 'opencode'
export type ObservabilityAnalysisSeverity = 'low' | 'medium' | 'high' | 'critical'
export type ObservabilityAnalysisPriority = 'p0' | 'p1' | 'p2' | 'p3'

export interface ObservabilityAnalysisFinding {
  title: string
  severity: ObservabilityAnalysisSeverity
  evidence: string[]
  probableCause: string | null
}

export interface ObservabilityAnalysisRecommendation {
  priority: ObservabilityAnalysisPriority
  action: string
  rationale: string
}

export interface ObservabilityAnalysisRisk {
  severity: ObservabilityAnalysisSeverity
  description: string
}

export interface ObservabilityAnalysisRequest {
  scope: ObservabilityAnalysisScope
  provider?: ObservabilityAnalysisProvider
  model?: string
  tenantId?: string
  timeframe?: string
  summary?: Record<string, unknown>
  alerts?: Array<Record<string, unknown>>
  funnel?: Record<string, unknown>
  samples?: Array<Record<string, unknown>>
  tracePreview?: Array<Record<string, unknown>>
  toolPreview?: Array<Record<string, unknown>>
  coherenceEvidence?: Record<string, unknown>
  effectivePolicy?: Record<string, unknown>
  observability?: Record<string, unknown>
  debug?: boolean
}

export interface ObservabilityAnalysisResponse {
  data: {
    summary: string
    findings: ObservabilityAnalysisFinding[]
    recommendations: ObservabilityAnalysisRecommendation[]
    risks: ObservabilityAnalysisRisk[]
    suspectAreas: string[]
    reproSteps: string[]
    testsToAdd: string[]
    codexInvestigationPrompt: string | null
    confidence: number
    provider: string
    model: string
    tokensUsed: number
    latencyMs: number
    bugPrompt?: string | null
    codeReviewPrompt?: string | null
  }
}

export interface ObservabilityAnalysisModelsResponse {
  data: {
    requestedProvider: ObservabilityAnalysisProvider
    provider: Exclude<ObservabilityAnalysisProvider, 'auto'>
    configured: boolean
    defaultModel: string
    models: string[]
    source: 'api' | 'fallback'
    reason?: string
  }
}

const getAdminHeaders = (): HeadersInit => {
  const token = localStorage.getItem('monitoring_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      ...getAdminHeaders(),
      ...(options.headers || {}),
    },
    body: options.body,
  })

  if (!response.ok) {
    let errorDetail = ''
    try {
      const errorJson = await response.json()
      errorDetail = errorJson.error || errorJson.message || ''
    } catch {
      // ignore
    }
    throw new Error(errorDetail ? `Trace request failed (${response.status}): ${errorDetail}` : `Trace request failed: ${response.status}`)
  }

  return response.json()
}

export type InvariantVerdict = 'true_positive' | 'false_positive'

export type InvariantSample = {
  traceId: string
  conversationId: string | null
  createdAt: string
  message: string | null
  invariantId: string
  details: Record<string, unknown> | null
  verdict: InvariantVerdict | null
}

export type InvariantVerdictStats = {
  invariantId: string
  truePositives: number
  falsePositives: number
  labelled: number
  falsePositiveRate: number | null
}

export type InvariantPromotionEvidence = InvariantVerdictStats & {
  canPromote: boolean
  blockers: string[]
}

export const traceService = {
  getTenants: () =>
    request<{ data: Array<{ id: string; name: string; slug: string }> }>('/admin/tenants'),

  getSearchLogSummary: (params: { tenantId: string; days?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.days ? { days: String(params.days) } : {}),
    })
    return request<{ data: { days: number; from: string; total: number; withResults: number; successRate: number; strategies: Array<{ strategy: string; count: number }> } }>(
      `/admin/search-log/summary?${search.toString()}`
    )
  },

  getSearchFailingQueries: (params: { tenantId: string; days?: number; limit?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.days ? { days: String(params.days) } : {}),
      ...(params.limit ? { limit: String(params.limit) } : {}),
    })
    return request<{ data: Array<{ originalQuery: string; freq: number; avgResults: number; avgReformulations: number }> }>(
      `/admin/search-log/failing-queries?${search.toString()}`
    )
  },

  getConversations: (tenantId: string) =>
    request<{
      data: Array<{
        id: string
        contactName: string
        waContact: string
        status: string
        mode: string
        lastMessage?: string
        updatedAt: string
      }>
    }>(`/admin/tenants/${tenantId}/conversations`),

  getTracesByConversation: (conversationId: string, params: { tenantId: string; limit?: number; page?: number } ) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.limit ? { limit: String(params.limit) } : {}),
      ...(params.page ? { page: String(params.page) } : {}),
    })

    return request<{ data: Array<TraceSummary>; pagination: { total: number; page: number; limit: number; pages: number } }>(
      `/admin/traces/conversation/${conversationId}?${search.toString()}`
    )
  },

  getTraceDetail: (traceId: string) =>
    request<TraceSummary & { steps: TraceStep[] }>(`/admin/traces/${traceId}`),

  getTraceDetailForTenant: (traceId: string, tenantId: string) => {
    const search = new URLSearchParams({ tenantId })
    return request<TraceSummary & { steps: TraceStep[] }>(`/admin/traces/${traceId}?${search.toString()}`)
  },

  subscribeToLiveTrace: (conversationId: string, tenantId: string, onStep: (step: any) => void) => {
    const token = localStorage.getItem('monitoring_token')
    const search = new URLSearchParams({
      ...(token ? { token } : {}),
      tenantId,
    })
    const url = `${API_BASE_URL}/admin/traces/live/${conversationId}${search.toString() ? `?${search.toString()}` : ''}`
    const es = new EventSource(url)

    es.onmessage = (event) => {
      onStep(JSON.parse(event.data))
    }

    es.onerror = () => {
      es.close()
    }

    return () => es.close()
  },

  resumeHitl: (payload: HitlResumePayload) =>
    request<HitlResumeResponse>('/admin/traces/hitl/resume', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getObservabilitySummary: (params: { tenantId: string; minutes?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.minutes ? { minutes: String(params.minutes) } : {}),
    })
    return request<ObservabilitySummaryResponse>(`/admin/debug/observability/summary?${search.toString()}`)
  },

  getObservabilityAlerts: (params: { tenantId: string; status?: 'active' | 'acknowledged' | 'resolved'; minutes?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.status ? { status: params.status } : {}),
      ...(params.minutes ? { minutes: String(params.minutes) } : {}),
    })
    return request<ObservabilityAlertResponse>(`/admin/debug/observability/alerts?${search.toString()}`)
  },

  ackObservabilityAlert: (params: { tenantId: string; alertId: string }) => {
    const search = new URLSearchParams({ tenantId: params.tenantId })
    return request<{ data: ObservabilityAlert }>(`/admin/debug/observability/alerts/${params.alertId}/acknowledge?${search.toString()}`, {
      method: 'POST',
    })
  },

  resolveObservabilityAlert: (params: { tenantId: string; alertId: string }) => {
    const search = new URLSearchParams({ tenantId: params.tenantId })
    return request<{ data: ObservabilityAlert }>(`/admin/debug/observability/alerts/${params.alertId}/resolve?${search.toString()}`, {
      method: 'POST',
    })
  },

  getInvariantSamples: (params: { tenantId: string; invariantId: string; minutes?: number; limit?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.minutes ? { minutes: String(params.minutes) } : {}),
      ...(params.limit ? { limit: String(params.limit) } : {}),
    })
    return request<{ data: InvariantSample[] }>(
      `/admin/debug/observability/invariants/${encodeURIComponent(params.invariantId)}/samples?${search.toString()}`
    )
  },

  getInvariantPromotion: (params: { tenantId: string; invariantId: string }) => {
    const search = new URLSearchParams({ tenantId: params.tenantId })
    return request<{ data: InvariantPromotionEvidence }>(
      `/admin/debug/observability/invariants/${encodeURIComponent(params.invariantId)}/promotion?${search.toString()}`
    )
  },

  recordInvariantVerdict: (params: {
    tenantId: string
    alertId: string
    traceId: string
    invariantId: string
    verdict: InvariantVerdict
  }) => {
    const search = new URLSearchParams({ tenantId: params.tenantId })
    return request<{ data: InvariantVerdictStats }>(
      `/admin/debug/observability/alerts/${encodeURIComponent(params.alertId)}/samples/${encodeURIComponent(params.traceId)}/verdict?${search.toString()}`,
      {
        method: 'POST',
        body: JSON.stringify({ invariantId: params.invariantId, verdict: params.verdict }),
      }
    )
  },

  getObservabilityFunnel: (params: { tenantId: string; minutes?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.minutes ? { minutes: String(params.minutes) } : {}),
    })
    return request<ObservabilityFunnelResponse>(`/admin/debug/observability/funnel?${search.toString()}`)
  },

  getObservabilityConversations: (params: { tenantId: string; limit?: number }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.limit ? { limit: String(params.limit) } : {}),
    })
    return request<ObservabilityConversationsResponse>(`/admin/debug/observability/conversations?${search.toString()}`)
  },

  analyzeObservability: (payload: ObservabilityAnalysisRequest) =>
    request<ObservabilityAnalysisResponse>('/admin/debug/ai-analysis', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  getObservabilityAnalysisModels: (provider: ObservabilityAnalysisProvider) => {
    const search = new URLSearchParams({ provider })
    return request<ObservabilityAnalysisModelsResponse>(`/admin/debug/ai-analysis/models?${search.toString()}`)
  },

  evaluateConversation: (params: {
    conversationId: string
    tenantId: string
    deepEvaluation?: boolean
    messages?: Array<{ role: 'user' | 'assistant'; content: string; timestamp?: string }>
  }) =>
    request<{ data: TestChatEvaluationResult }>(
      `/admin/debug/observability/conversations/${params.conversationId}/evaluate`,
      {
        method: 'POST',
        body: JSON.stringify({
          tenantId: params.tenantId,
          deepEvaluation: params.deepEvaluation,
          messages: params.messages,
        }),
      }
    ),

  getTestChatSession: (params: { tenantId: string; visitorId?: string }) => {
    const search = new URLSearchParams({
      tenantId: params.tenantId,
      ...(params.visitorId ? { visitorId: params.visitorId } : {}),
    })
    return request<TestWebchatSession>(`/admin/debug/observability/test-chat/session?${search.toString()}`)
  },

  sendTestChatMessage: (payload: {
    tenantId: string
    visitorId: string
    text: string
    visitorName?: string
    imageUrl?: string
    mediaUrl?: string
    mediaMimetype?: string
    widgetToken?: string
  }) =>
    request<SendTestMessageResult>('/admin/debug/observability/test-chat/message', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  resetTestChatSession: (params: { tenantId: string; visitorId: string }) =>
    request<{ ok: boolean }>('/admin/debug/observability/test-chat/reset', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  evaluateTestChat: (payload: {
    tenantId: string
    visitorId?: string
    conversationId?: string
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string; timestamp?: string | number }>
    stageHistory?: string[]
    finalStage?: string
    selectedProduct?: any
    deepEvaluation?: boolean
  }) =>
    request<TestChatEvaluationResult>('/admin/debug/observability/test-chat/evaluate', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  replayTestConversation: (payload: {
    tenantId: string
    sourceConversationId?: string
    turns?: Array<{ text: string; mediaUrl?: string; mediaMimetype?: string }>
    mode?: 'full' | 'until_turn'
    maxTurns?: number
    evaluate?: boolean
    visitorId?: string
  }) =>
    request<{
      ok: boolean
      tenantId: string
      conversationId: string
      visitorId: string
      totalTurns: number
      durationMs: number
      turns: Array<{
        turnIndex: number
        inboundText: string
        reply: string
        mediaUrl?: string | null
        stage?: string | null
        intent?: string | null
        selectedProduct?: any
        candidateProducts?: any[]
        activeOrderDraft?: any
        traceId?: string | null
        supervisorReflexionExecuted?: boolean
        traceSteps?: any[]
      }>
      evaluation?: TestChatEvaluationResult | null
    }>('/admin/debug/observability/test-chat/replay', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}

